import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Invite Members - Invitation system
 *
 * Features:
 * - Email invite form
 * - Invite code generator
 * - Pending invitations list
 */

export default function OrgInviteMembers({ organization, members }) {
  const [emailInvite, setEmailInvite] = useState({ email: '', role: 'member' });
  const [inviteCode, setInviteCode] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [codeSettings, setCodeSettings] = useState({ expiryDays: 7, usageLimit: 10 });

  useEffect(() => {
    if (organization?.id) {
      loadPendingInvites();
      loadInviteCode();
    }
  }, [organization?.id]);

  const loadPendingInvites = async () => {
    try {
      const invitesRef = collection(db, `organizations/${organization.id}/invitations`);
      const q = query(invitesRef, where('status', '==', 'pending'));
      const snapshot = await getDocs(q);

      const invites = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setPendingInvites(invites);
    } catch (error) {
      console.error('Error loading invites:', error);
    }
  };

  const loadInviteCode = async () => {
    try {
      const invitesRef = collection(db, `organizations/${organization.id}/invitations`);
      const q = query(invitesRef, where('type', '==', 'code'));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const codeDoc = snapshot.docs[0];
        const data = codeDoc.data();
        if (data.expiresAt?.toDate?.() > new Date()) {
          setInviteCode({
            id: codeDoc.id,
            ...data,
            expiresAt: data.expiresAt?.toDate?.()
          });
        }
      }
    } catch (error) {
      console.error('Error loading invite code:', error);
    }
  };

  const handleEmailInvite = async (e) => {
    e.preventDefault();
    if (!emailInvite.email) return;

    // Check if already a member
    if (members.some(m => m.email?.toLowerCase() === emailInvite.email.toLowerCase())) {
      alert('This email is already a member of the organization');
      return;
    }

    try {
      setLoading(true);
      const invitesRef = collection(db, `organizations/${organization.id}/invitations`);
      await addDoc(invitesRef, {
        type: 'email',
        email: emailInvite.email.toLowerCase(),
        role: emailInvite.role,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
      });

      setEmailInvite({ email: '', role: 'member' });
      loadPendingInvites();
      alert(`Invitation sent to ${emailInvite.email}`);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCode = async () => {
    try {
      setLoading(true);
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const invitesRef = collection(db, `organizations/${organization.id}/invitations`);

      // Delete old code if exists
      if (inviteCode?.id) {
        await deleteDoc(doc(db, `organizations/${organization.id}/invitations/${inviteCode.id}`));
      }

      const docRef = await addDoc(invitesRef, {
        type: 'code',
        code,
        usageLimit: codeSettings.usageLimit,
        usageCount: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + codeSettings.expiryDays * 24 * 60 * 60 * 1000)
      });

      setInviteCode({
        id: docRef.id,
        code,
        usageLimit: codeSettings.usageLimit,
        usageCount: 0,
        expiresAt: new Date(Date.now() + codeSettings.expiryDays * 24 * 60 * 60 * 1000)
      });
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Failed to generate invite code');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (inviteCode?.code) {
      navigator.clipboard.writeText(inviteCode.code);
      alert('Invite code copied to clipboard!');
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await deleteDoc(doc(db, `organizations/${organization.id}/invitations/${inviteId}`));
      loadPendingInvites();
    } catch (error) {
      console.error('Error canceling invite:', error);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Invite Members</h2>
        <p className="text-white/50 text-sm mt-1">Add team members to your organization</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Invite */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#06c]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">Email Invite</h3>
              <p className="text-white/50 text-xs">Send direct invitations</p>
            </div>
          </div>

          <form onSubmit={handleEmailInvite} className="space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Email Address</label>
              <input
                type="email"
                value={emailInvite.email}
                onChange={(e) => setEmailInvite({ ...emailInvite, email: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50"
                placeholder="colleague@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Role</label>
              <select
                value={emailInvite.role}
                onChange={(e) => setEmailInvite({ ...emailInvite, role: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#06c]/50"
              >
                <option value="member" className="bg-[#1c1c1e]">Member</option>
                <option value="admin" className="bg-[#1c1c1e]">Admin</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading || !emailInvite.email}
              className="w-full py-3 bg-gradient-to-r from-[#06c] to-purple-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        </div>

        {/* Invite Code */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">Invite Code</h3>
              <p className="text-white/50 text-xs">Generate a shareable code</p>
            </div>
          </div>

          {inviteCode ? (
            <div className="space-y-4">
              {/* Code Display */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <code className="text-2xl font-mono font-bold text-white tracking-wider">
                    {inviteCode.code}
                  </code>
                  <button
                    onClick={copyCode}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Code Info */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/50">
                  Used: {inviteCode.usageCount || 0} / {inviteCode.usageLimit}
                </span>
                <span className="text-white/50">
                  Expires: {formatDate(inviteCode.expiresAt)}
                </span>
              </div>

              <button
                onClick={generateInviteCode}
                disabled={loading}
                className="w-full py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                Generate New Code
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Expires After</label>
                  <select
                    value={codeSettings.expiryDays}
                    onChange={(e) => setCodeSettings({ ...codeSettings, expiryDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none"
                  >
                    <option value={1} className="bg-[#1c1c1e]">1 day</option>
                    <option value={7} className="bg-[#1c1c1e]">7 days</option>
                    <option value={30} className="bg-[#1c1c1e]">30 days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Usage Limit</label>
                  <select
                    value={codeSettings.usageLimit}
                    onChange={(e) => setCodeSettings({ ...codeSettings, usageLimit: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none"
                  >
                    <option value={5} className="bg-[#1c1c1e]">5 uses</option>
                    <option value={10} className="bg-[#1c1c1e]">10 uses</option>
                    <option value={25} className="bg-[#1c1c1e]">25 uses</option>
                    <option value={100} className="bg-[#1c1c1e]">100 uses</option>
                  </select>
                </div>
              </div>

              <button
                onClick={generateInviteCode}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Invite Code'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-white font-semibold">Pending Invitations ({pendingInvites.length})</h3>
        </div>

        {pendingInvites.length > 0 ? (
          <div className="divide-y divide-white/5">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <div className="text-white font-medium text-sm">{invite.email}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-white/50 text-xs capitalize">{invite.role}</span>
                    <span className="text-white/30 text-xs">•</span>
                    <span className="text-white/50 text-xs">Sent {formatDate(invite.createdAt)}</span>
                  </div>
                </div>
                <button
                  onClick={() => cancelInvite(invite.id)}
                  className="px-3 py-1.5 text-red-400 text-xs hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-white/40 text-sm">No pending invitations</div>
          </div>
        )}
      </div>
    </div>
  );
}
